"use client";

import TableSortingIcon from "@/components/common/TableSortingIcon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Customer } from "@/types/models/customer";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import CustomerActionsCell from "./CustomerActionsCell";
import { Avatar } from "@/components/ui/avatar";
import { formatCurrency } from "@/utils/currency";

// Helper function to get time-based badge color (similar to orders page style)
const getLastOrderColor = (lastOrderDate: string | null | undefined) => {
  if (!lastOrderDate)
    return "bg-gray-500/20 text-gray-600 hover:bg-gray-500/20";

  const now = new Date();
  const orderDate = new Date(lastOrderDate);
  const diffInDays = Math.floor(
    (now.getTime() - orderDate.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffInDays <= 7)
    return "bg-green-500/20 text-green-600 hover:bg-green-500/20";
  if (diffInDays <= 30)
    return "bg-blue-500/20 text-blue-600 hover:bg-blue-500/20";
  if (diffInDays <= 90)
    return "bg-yellow-500/20 text-yellow-600 hover:bg-yellow-500/20";
  return "bg-red-500/20 text-red-600 hover:bg-red-500/20";
};

const getLastActiveColor = (lastActiveDate: string | null | undefined) => {
  if (!lastActiveDate)
    return "bg-gray-500/20 text-gray-600 hover:bg-gray-500/20";

  const now = new Date();
  const activeDate = new Date(lastActiveDate);
  const diffInDays = Math.floor(
    (now.getTime() - activeDate.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffInDays <= 30)
    return "bg-green-500/20 text-green-600 hover:bg-green-500/20"; // Last month - Green
  if (diffInDays <= 90)
    return "bg-orange-500/20 text-orange-600 hover:bg-orange-500/20"; // 1-3 months - Orange
  return "bg-red-500/20 text-red-600 hover:bg-red-500/20"; // Over 3 months - Red
};

export const columns: ColumnDef<Customer>[] = [
  {
    id: "select",
    size: 50,
    minSize: 40,
    maxSize: 60,
    header: ({ table }) => {
      const { rows } = table.getRowModel();
      const selectedRows = table.getSelectedRowModel().rows;
      const isAllSelected =
        rows.length > 0 && selectedRows.length === rows.length;
      const isSomeSelected = selectedRows.length > 0 && !isAllSelected;

      return (
        <div className="flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <Checkbox
            checked={
              isAllSelected ? true : isSomeSelected ? "indeterminate" : false
            }
            onCheckedChange={(value) => {
              if (value) {
                table.toggleAllPageRowsSelected(true);
              } else {
                table.toggleAllPageRowsSelected(false);
              }
            }}
            aria-label="Select all"
            className="!bg-transparent border-foreground text-foreground data-[state=checked]:text-foreground"
          />
        </div>
      );
    },
    cell: ({ row }) => (
      <div className="flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="!bg-transparent border-foreground text-foreground data-[state=checked]:text-foreground"
        />
      </div>
    ),
  },
  {
    accessorKey: "name",
    size: 250,
    minSize: 200,
    maxSize: 350,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Customer</span>
          <div
            className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const customer = row.original;
      const firstName = customer?.first_name || "";
      const lastName = customer?.last_name || "";
      const email = customer?.email || "";
      const fullName =
        firstName || lastName
          ? `${firstName} ${lastName}`.trim()
          : "Unknown Customer";

      return (
        <div className="flex items-center gap-3">
          <Avatar name={fullName} className="h-8 w-8" />
          <div>
            <h3 className="text-sm font-semibold truncate max-w-[200px]">
              {fullName}
            </h3>
            {email && (
              <span className="text-xs text-muted-foreground truncate block max-w-[200px]">
                {email}
              </span>
            )}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "phone_number",
    size: 140,
    minSize: 120,
    maxSize: 180,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Phone</span>
          <div
            className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const phoneNumber = row.getValue("phone_number") as string;
      return (
        <div>
          <span className="text-sm font-medium">
            {phoneNumber || "No phone"}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "total_orders",
    size: 100,
    minSize: 80,
    maxSize: 120,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Orders</span>
          <div
            className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const totalOrders = row.getValue("total_orders") as number;
      return (
        <div>
          <span className="text-sm font-semibold">{totalOrders || 0}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "total_spent",
    size: 120,
    minSize: 100,
    maxSize: 150,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Revenue</span>
          <div
            className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const totalSpent = row.getValue("total_spent") as number;
      return (
        <div>
          <span className="text-sm font-semibold">
            {formatCurrency(totalSpent || 0)}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "last_order_date",
    size: 140,
    minSize: 120,
    maxSize: 180,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Last Order</span>
          <div
            className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const lastOrderDate = row.getValue("last_order_date") as string;

      if (!lastOrderDate) {
        return (
          <Badge
            variant="secondary"
            className="bg-gray-500/20 text-gray-600 hover:bg-gray-500/20 rounded-full px-[10px] py-[5px]"
          >
            Never
          </Badge>
        );
      }

      const badgeColor = getLastOrderColor(lastOrderDate);
      const date = new Date(lastOrderDate);
      const formattedDate = format(date, "MMM dd");

      return (
        <Badge
          variant="secondary"
          className={`${badgeColor} rounded-full px-[10px] py-[5px]`}
        >
          {formattedDate}
        </Badge>
      );
    },
  },
  {
    accessorKey: "default_address_id",
    size: 200,
    minSize: 160,
    maxSize: 250,
    header: () => <span className="text-base font-bold">Default Address</span>,
    cell: ({ row }) => {
      const addresses = row.original.customer_addresses;
      const defaultAddressId = row.getValue("default_address_id") as string;
      const defaultAddress = addresses?.find(
        (addr) => addr.id === defaultAddressId,
      );

      if (!defaultAddress) {
        return (
          <div>
            <span className="text-sm text-muted-foreground">
              No default address
            </span>
          </div>
        );
      }

      return (
        <div>
          <div className="text-sm font-medium truncate max-w-[180px]">
            {defaultAddress.address_line_one}
          </div>
          <div className="text-xs text-muted-foreground">
            {defaultAddress.city}, {defaultAddress.state} -{" "}
            {defaultAddress.postal_code}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "last_active_date",
    size: 120,
    minSize: 100,
    maxSize: 150,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Last Active</span>
          <div
            className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const lastActiveDate = row.getValue("last_active_date") as string;

      if (!lastActiveDate) {
        return (
          <Badge
            variant="secondary"
            className="bg-gray-500/20 text-gray-600 hover:bg-gray-500/20 rounded-full px-[10px] py-[5px]"
          >
            Unknown
          </Badge>
        );
      }

      const badgeColor = getLastActiveColor(lastActiveDate);
      const date = new Date(lastActiveDate);
      const formattedDate = format(date, "MMM dd");

      return (
        <Badge
          variant="secondary"
          className={`${badgeColor} rounded-full px-[10px] py-[5px]`}
        >
          {formattedDate}
        </Badge>
      );
    },
  },
  {
    accessorKey: "actions",
    size: 190,
    minSize: 150,
    maxSize: 250,
    header: () => <span className="text-base font-bold">Actions</span>,
    cell: ({ row }) => <CustomerActionsCell customer={row.original} />,
  },
];
